import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChartPieSlice, CrownSimple, PiggyBank } from '@phosphor-icons/react';
import { InnenKarte, KartenTitel, KpiReihe, KpiTile, LeerZustand } from '../../../../../components/immobilien/ui';
import { eur, heuteIso } from '../../../../../lib/immobilien/format';
import { mieterName } from '../../../../../lib/immobilien/objektDetail';
import { istWohnend } from '../../../../../lib/dashboard/leerstand';
import { baueAktivitaeten } from '../../../../../lib/immobilien/aktivitaet';
import { schluesselJeEinheit } from '../../../../../lib/immobilien/schluessel';
import { TicketsBereich } from '../../gemeinsam/TicketsBereich';
import { EtvBereich, VersicherungBereich, VertraegeBereich, WartungBereich } from '../../gemeinsam/FachBereiche';
import { AktivitaetBereich, MieterhoehungBereich, SchluesselBereich } from '../../gemeinsam/WeitereBereiche';
import { useObjektSeite } from '../ObjektSeitenKontext';

function useNamen() {
  const { daten, uebersicht } = useObjektSeite();
  return useMemo(() => {
    const einheit = new Map(uebersicht.map(e => [e.id, e.bezeichnung]));
    const mieter = new Map(daten.mieter.map(m => [m.id, mieterName(m)]));
    return {
      einheit: (id: string | null) => (id ? einheit.get(id) ?? null : null),
      mieter: (id: string | null) => (id ? mieter.get(id) ?? null : null),
    };
  }, [daten.mieter, uebersicht]);
}

export function TicketsPanel() {
  const { daten, neuLaden } = useObjektSeite();
  const namen = useNamen();
  return <TicketsBereich tickets={daten.tickets} objektId={daten.objekt.id} einheitName={namen.einheit} onGeaendert={() => neuLaden('tickets')} />;
}

export function WartungPanel() {
  const { daten, neuLaden, oeffnePanel } = useObjektSeite();
  return (
    <WartungBereich objektId={daten.objekt.id} letztePruefung={daten.objekt.letzte_pruefung ?? null} termine={daten.termine} dokumente={daten.fachDokumente}
      dienstleister={daten.dienstleister} heute={heuteIso()} onTerminGeaendert={() => neuLaden('termine')} onPruefungAendern={() => oeffnePanel('stamm', { bearbeiten: true })} />
  );
}

export function VersicherungPanel() {
  const { daten } = useObjektSeite();
  return <VersicherungBereich objektId={daten.objekt.id} dokumente={daten.fachDokumente} dienstleister={daten.dienstleister} />;
}

export function VertraegePanel() {
  const { daten, oeffnePanel } = useObjektSeite();
  const namen = useNamen();
  return (
    <VertraegeBereich vertraege={daten.vertraege} dienstleister={daten.dienstleister} links={daten.dienstleisterLinks}
      mieterName={id => namen.mieter(id) ?? 'Mieter'} onDienstleister={() => oeffnePanel('dienstleister')} />
  );
}

export function EtvPanel() {
  const { daten, neuLaden } = useObjektSeite();
  return <EtvBereich objektId={daten.objekt.id} termine={daten.termine} heute={heuteIso()} onTerminGeaendert={() => neuLaden('termine')} />;
}

export function SchluesselPanel() {
  const { daten, uebersicht } = useObjektSeite();
  const namen = useNamen();
  const staende = useMemo(() => [...schluesselJeEinheit(daten.uebergaben).values()], [daten.uebergaben]);
  return <SchluesselBereich staende={staende} einheiten={uebersicht.map(e => ({ id: e.id, bezeichnung: e.bezeichnung }))} mieterName={namen.mieter} />;
}

export function AktivitaetPanel() {
  const { daten } = useObjektSeite();
  const namen = useNamen();
  const liste = useMemo(() => baueAktivitaeten({
    notizen: daten.notizen,
    dokumente: daten.dokumente,
    tickets: daten.tickets,
    termine: daten.termine,
    uebergaben: daten.uebergaben.map(u => ({ ...u, einheit: namen.einheit(u.einheit_id) })),
    mieter: daten.mieter.map(m => ({ id: m.id, name: mieterName(m), mietbeginn: m.mietbeginn, mietende: m.mietende, einheit: namen.einheit(m.einheit_id) })),
  }, new Date().toISOString()), [daten, namen]);
  return <AktivitaetBereich aktivitaeten={liste} />;
}

export function MieterhoehungPanel() {
  const { daten, uebersicht } = useObjektSeite();
  const navigate = useNavigate();
  // § 558 BGB gilt nur für Wohnraum
  const zeilen = useMemo(() => uebersicht.filter(e => e.art === 'wohnung').flatMap(e => daten.mieter
    .filter(m => m.einheit_id === e.id && istWohnend(m.status))
    .map(m => ({
      id: m.id, einheit: e.bezeichnung, mieter: mieterName(m), flaeche: e.flaeche, roh: m,
      onOeffnen: () => navigate(`/admin/objekte/${encodeURIComponent(daten.objekt.id)}/einheiten/${encodeURIComponent(e.id)}?bereich=mieterh`),
    }))), [daten, uebersicht, navigate]);
  return <MieterhoehungBereich zeilen={zeilen} heute={heuteIso()} />;
}

/** „WEG · MEA & Hausgeld“ und „Wirtschaftsplan & Rücklage“: Werte je Einheit aus einheit_eigentuemer. */
export function WegPanel({ fokus }: { fokus: 'weg' | 'wplan' }) {
  const { daten, uebersicht } = useObjektSeite();
  const navigate = useNavigate();
  const zeilen = useMemo(() => {
    const reihen = new Map(daten.weg.map(w => [w.einheit_id, w]));
    return uebersicht.map(e => ({ e, w: reihen.get(e.id) ?? null }));
  }, [daten.weg, uebersicht]);
  const mea = daten.weg.reduce((s, w) => s + (w.mea ?? 0), 0);
  const hausgeld = daten.weg.reduce((s, w) => s + (w.hausgeld_vorschuss ?? 0), 0);
  const ruecklage = daten.weg.reduce((s, w) => s + (w.ruecklage_vorschuss ?? 0), 0);
  const zurEinheit = (id: string) => navigate(`/admin/objekte/${encodeURIComponent(daten.objekt.id)}/einheiten/${encodeURIComponent(id)}?bereich=hausgeld`);

  if (daten.weg.length === 0) {
    return <LeerZustand icon={CrownSimple} titel="Keine Eigentümer-Einträge" text="MEA, Hausgeld und Rücklage werden je Einheit im Bereich „Hausgeld“ bzw. „Eigentümer“ der Einheit erfasst." />;
  }
  return (
    <>
      <KpiReihe>
        {fokus === 'weg' ? (
          <>
            <KpiTile label="MEA gesamt" wert={mea.toLocaleString('de-DE')} sub={`${daten.weg.length} von ${uebersicht.length} Einheiten erfasst`} icon={ChartPieSlice} ton="acc" />
            <KpiTile label="Hausgeld" wert={eur(hausgeld)} sub="Vorschüsse pro Monat" icon={PiggyBank} />
            <KpiTile label="Rücklage" wert={eur(ruecklage)} sub="Zuführung pro Monat" icon={PiggyBank} />
          </>
        ) : (
          <>
            <KpiTile label="Hausgeld / Jahr" wert={eur(hausgeld * 12)} sub="Summe der Vorschüsse (Soll)" icon={PiggyBank} ton="acc" />
            <KpiTile label="Rücklage / Jahr" wert={eur(ruecklage * 12)} sub="geplante Zuführung (Soll)" icon={PiggyBank} />
            <KpiTile label="Gesamt / Jahr" wert={eur((hausgeld + ruecklage) * 12)} sub="Wirtschaftsplan (Vorschüsse)" icon={ChartPieSlice} />
          </>
        )}
      </KpiReihe>
      <InnenKarte>
        <KartenTitel rechts="Klick öffnet die Einheit">{fokus === 'weg' ? 'Je Einheit' : 'Vorschüsse je Einheit und Jahr'}</KartenTitel>
        <div className="flex flex-col">
          {zeilen.map(({ e, w }) => (
            <button key={e.id} type="button" onClick={() => zurEinheit(e.id)} className="immo-hover-flaeche flex items-center text-left cursor-pointer flex-wrap"
              style={{ gap: 10, padding: '10px 8px', borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink)', borderBottom: '1px solid var(--line)' }}>
              <span className="flex flex-col flex-1 min-w-0" style={{ flexBasis: 160 }}>
                <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{e.bezeichnung}</span>
                <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{w?.name || 'kein Eigentümer-Eintrag'}</span>
              </span>
              {fokus === 'weg' && <span className="immo-zahl" style={{ fontSize: 13, minWidth: 70, textAlign: 'right' }}>{w?.mea != null ? `MEA ${w.mea.toLocaleString('de-DE')}` : '–'}</span>}
              <span className="immo-zahl" style={{ fontSize: 13, fontWeight: 800, minWidth: 110, textAlign: 'right' }}>
                {w?.hausgeld_vorschuss != null ? eur(w.hausgeld_vorschuss * (fokus === 'wplan' ? 12 : 1)) : '–'}
              </span>
              <span className="immo-zahl" style={{ fontSize: 13, minWidth: 110, textAlign: 'right', color: 'var(--ink2)' }}>
                {w?.ruecklage_vorschuss != null ? `RL ${eur(w.ruecklage_vorschuss * (fokus === 'wplan' ? 12 : 1))}` : '–'}
              </span>
            </button>
          ))}
        </div>
      </InnenKarte>
      {fokus === 'wplan' && (
        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
          Soll aus den Vorschüssen je Einheit. Wirtschaftsplan mit Kostenpositionen, Rücklagenstand und Jahresabrechnung kommen mit der Buchhaltung.
        </span>
      )}
    </>
  );
}
